import React, { useEffect, useState, useRef } from 'react';
import axios from '../utils/axiosInstance';
import { Button } from '@/components/ui/button';
import { PageLoader } from '@/components/ui/page-loader';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Cropper } from '@/components/ui/image-crop';
import { Upload, RefreshCw, Trash2, Palette, Star } from 'lucide-react';
import { BASE_URL } from '@/config/constants';
import { toast } from 'sonner';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    type DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Input } from '@/components/ui/input';

interface MasterTheme {
    _id: string;
    title: string;
    image: string;
    isPaid: boolean;
    price: number;
    allocatedDomains: string[];
    isActive: boolean;
    order: number;
    createdAt: string;
    updatedAt: string;
}

interface Domain {
    _id: string;
    slug: string;
    url: string;
    is_active: boolean;
}

const emptyTheme = {
    title: '',
    image: '',
    isPaid: false,
    price: 0,
    allocatedDomains: [] as string[],
    isActive: true,
};

interface SortableRowProps {
    theme: MasterTheme;
    onEdit: (theme: MasterTheme) => void;
    onDelete: (id: string) => void;
    onToggleActive: (id: string, currentStatus: boolean) => void;
    onSetDefault: (id: string) => void;
    togglingId: string | null;
    defaultThemeIds: string[];
    settingDefaultId: string | null;
}

function SortableRow({ theme, onEdit, onDelete, onToggleActive, onSetDefault, togglingId, defaultThemeIds, settingDefaultId }: SortableRowProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: theme._id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <TableRow
            ref={setNodeRef}
            style={style}
            className="transition-colors"
        >
            <TableCell className="w-10 select-none cursor-grab">
                <div className="flex items-center gap-2" {...attributes} {...listeners}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground">
                        <circle cx="9" cy="12" r="1"/><circle cx="9" cy="5" r="1"/><circle cx="9" cy="19" r="1"/>
                        <circle cx="15" cy="12" r="1"/><circle cx="15" cy="5" r="1"/><circle cx="15" cy="19" r="1"/>
                    </svg>
                    <span className="w-4 text-muted-foreground font-medium">{theme.order + 1}</span>
                </div>
            </TableCell>
            <TableCell>
                <img
                    src={theme.image}
                    alt={theme.title}
                    className="w-20 h-12 object-cover rounded-lg"
                />
            </TableCell>
            <TableCell className="font-semibold">{theme.title}</TableCell>
            <TableCell>
                <div className="flex flex-col gap-1">
                    <span className={`px-2 py-1 rounded text-xs font-medium inline-block w-fit ${
                        theme.isPaid 
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' 
                            : 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                    }`}>
                        {theme.isPaid ? 'Paid' : 'Free'}
                    </span>
                    {theme.isPaid && theme.price > 0 && (
                        <span className="text-sm font-semibold text-foreground">${theme.price.toFixed(2)}</span>
                    )}
                </div>
            </TableCell>
            <TableCell>
                {theme.allocatedDomains.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                        {theme.allocatedDomains.map((slug) => (
                            <span
                                key={slug}
                                className="px-2 py-0.5 bg-secondary text-xs rounded"
                            >
                                {slug}
                            </span>
                        ))}
                    </div>
                ) : (
                    <span className="text-muted-foreground text-sm">No domains</span>
                )}
            </TableCell>
            <TableCell>
                <div className="text-xs text-muted-foreground">
                    {new Date(theme.createdAt).toLocaleDateString()}
                </div>
            </TableCell>
            <TableCell>
                <div className="flex items-center space-x-2">
                    <Switch
                        checked={theme.isActive}
                        onCheckedChange={() => onToggleActive(theme._id, theme.isActive)}
                        aria-label="Toggle theme active state"
                        disabled={togglingId === theme._id}
                    />
                    <span className={theme.isActive ? 'status-label-active' : 'status-label-inactive'}>
                        {theme.isActive ? 'Active' : 'Inactive'}
                    </span>
                </div>
            </TableCell>
            <TableCell>
                <div className="flex gap-2">
                    <Button
                        variant={defaultThemeIds.includes(theme._id) ? 'default' : 'outline'}
                        size="icon-sm"
                        onClick={() => onSetDefault(theme._id)}
                        disabled={settingDefaultId === theme._id}
                        title={defaultThemeIds.includes(theme._id) ? 'Remove from free defaults' : 'Add to free defaults'}
                    >
                        <Star className={`size-4 ${defaultThemeIds.includes(theme._id) ? 'fill-current' : ''}`} />
                    </Button>
                    <Button
                        variant="outline"
                        size="icon-sm"
                        onClick={() => onEdit(theme)}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                    </Button>
                    <Button
                        variant="destructive"
                        size="icon-sm"
                        onClick={() => onDelete(theme._id)}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            <line x1="10" y1="11" x2="10" y2="17"></line>
                            <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                    </Button>
                </div>
            </TableCell>
        </TableRow>
    );
}

const MasterThemeManager: React.FC = () => {
    const [themes, setThemes] = useState<MasterTheme[]>([]);
    const [domains, setDomains] = useState<Domain[]>([]);
    const [form, setForm] = useState(emptyTheme);
    const [loading, setLoading] = useState(true);
    const [addOpen, setAddOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editId, setEditId] = useState<string | null>(null);
    const [deleteId, setDeleteId] = useState<string | null>(null);
    const [error, setError] = useState('');
    const [uploadStep, setUploadStep] = useState<'idle' | 'cropping'>('idle');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [togglingId, setTogglingId] = useState<string | null>(null);
    const [defaultThemeIds, setDefaultThemeIds] = useState<string[]>([]);
    const [settingDefaultId, setSettingDefaultId] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const cropperRef = useRef<any>(null);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const fetchThemes = () => {
        setLoading(true);
        axios
            .get(`${BASE_URL}/api/master-theme`)
            .then((res) => setThemes(res.data))
            .catch((err) => {
                toast.error(err.response?.data?.message || 'Failed to fetch themes');
            })
            .finally(() => setLoading(false));
    };

    const fetchDomains = () => {
        axios
            .get(`${BASE_URL}/api/domain`)
            .then((res) => setDomains(res.data.filter((d: Domain) => d.is_active)))
            .catch((err) => {
                console.error('Failed to fetch domains:', err);
            });
    };

    const fetchGlobalSettings = () => {
        axios
            .get('/api/domain/settings/global')
            .then((res) => {
                const arr: string[] = (res.data?.defaultFreeThemes || []).map((id: any) => id.toString());
                // Also include the legacy singular field if not already in the array
                const singular = res.data?.defaultFreeTheme?.toString();
                if (singular && !arr.includes(singular)) arr.unshift(singular);
                setDefaultThemeIds(arr);
            })
            .catch(() => {});
    };

    useEffect(() => {
        fetchThemes();
        fetchDomains();
        fetchGlobalSettings();
    }, []);

    const handleSetDefault = async (themeId: string) => {
        setSettingDefaultId(themeId);
        const isCurrently = defaultThemeIds.includes(themeId);
        const newIds = isCurrently
            ? defaultThemeIds.filter(id => id !== themeId)
            : [...defaultThemeIds, themeId];
        // First entry in the array is the primary (highest priority) free theme
        try {
            await axios.put('/api/domain/settings/global', {
                defaultFreeThemes: newIds,
                // Keep singular field in sync with first entry for legacy consumers
                defaultFreeTheme: newIds[0] ?? null,
            });
            setDefaultThemeIds(newIds);
            const theme = themes.find(t => t._id === themeId);
            const action = isCurrently ? 'removed from' : 'added to';
            toast.success(`"${theme?.title || 'Theme'}" ${action} free defaults`);
        } catch (err: any) {
            toast.error(err.response?.data?.message || 'Failed to update free defaults');
        } finally {
            setSettingDefaultId(null);
        }
    };

    const resetUploadState = () => {
        setUploadStep('idle');
        setSelectedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const openFileUpload = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        fileInputRef.current?.click();
    };

    const openCropperForExisting = () => {
        if (form.image) {
            fetch(form.image)
                .then(res => res.blob())
                .then(blob => {
                    const file = new File([blob], 'theme-image.png', { type: blob.type });
                    setSelectedFile(file);
                    setUploadStep('cropping');
                })
                .catch(() => {
                    toast.error('Failed to load image for editing');
                });
        }
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const validFormats = ['image/png', 'image/jpeg'];
        const maxSize = 2 * 1024 * 1024;

        if (!validFormats.includes(file.type)) {
            setError('Only .png and .jpeg files are allowed.');
            toast.error('Only .png and .jpeg files are allowed.');
            return;
        }

        if (file.size > maxSize) {
            setError('File size must be less than 2MB.');
            toast.error('File size must be less than 2MB.');
            return;
        }

        setError('');
        setSelectedFile(file);
        setUploadStep('cropping');
    };

    const handleCropComplete = async (croppedImage: string) => {
        if (!croppedImage) return;

        setUploading(true);
        try {
            const response = await fetch(croppedImage);
            const blob = await response.blob();

            const formData = new FormData();
            formData.append('file', blob, 'cropped-theme.png');

            const res = await axios.post(BASE_URL + '/api/images/upload', formData);
            setForm((prev) => ({ ...prev, image: res.data.url }));
            resetUploadState();
            const isUpdate = form.image ? true : false;
            toast.success(isUpdate ? 'Image updated successfully!' : 'Image uploaded successfully!');
        } catch (err) {
            setError('Image upload failed.');
            toast.error('Image upload failed.');
        } finally {
            setUploading(false);
        }
    };

    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleDomainToggle = (domainSlug: string) => {
        setForm((prev) => {
            const allocatedDomains = prev.allocatedDomains.includes(domainSlug)
                ? prev.allocatedDomains.filter((slug) => slug !== domainSlug)
                : [...prev.allocatedDomains, domainSlug];
            return { ...prev, allocatedDomains };
        });
    };

    const handleAdd = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        
        if (!form.image) {
            setError('Please upload an image');
            toast.error('Please upload an image');
            return;
        }

        setSaving(true);
        try {
            const response = await axios.post(`${BASE_URL}/api/master-theme`, form);
            setAddOpen(false);
            setForm(emptyTheme);
            resetUploadState();
            toast.success('Theme added successfully!');
            if (response.data) {
                setThemes(prev => [...prev, response.data]);
            } else {
                fetchThemes();
            }
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || 'Failed to add theme';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (item: MasterTheme) => {
        setEditId(item._id);
        setForm({
            title: item.title,
            image: item.image,
            isPaid: item.isPaid,
            price: item.price || 0,
            allocatedDomains: item.allocatedDomains,
            isActive: item.isActive,
        });
        setEditOpen(true);
        setError('');
        resetUploadState();
    };

    const handleEditSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        
        if (!form.image) {
            setError('Please upload an image');
            toast.error('Please upload an image');
            return;
        }

        setSaving(true);
        const currentEditId = editId;
        try {
            const response = await axios.put(`${BASE_URL}/api/master-theme/${editId}`, form);
            setEditOpen(false);
            setEditId(null);
            setForm(emptyTheme);
            resetUploadState();
            toast.success('Theme updated successfully!');
            if (response.data) {
                setThemes(prev => prev.map(theme =>
                    theme._id === currentEditId ? { ...theme, ...response.data } : theme
                ));
            } else {
                fetchThemes();
            }
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || 'Failed to update theme';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteId) return;
        const deletedTheme = themes.find(theme => theme._id === deleteId);
        // Optimistic removal
        setThemes(prev => prev.filter(theme => theme._id !== deleteId));
        setDeleteId(null);
        try {
            await axios.delete(`${BASE_URL}/api/master-theme/${deleteId}`);
            toast.success('Theme deleted successfully!');
        } catch (err: any) {
            // Revert on error
            if (deletedTheme) {
                setThemes(prev => [...prev, deletedTheme].sort((a, b) => a.order - b.order));
            }
            const errorMessage = err.response?.data?.message || 'Failed to delete theme';
            toast.error(errorMessage);
        }
    };

    const handleToggleActive = async (id: string, currentStatus: boolean) => {
        setTogglingId(id);
        // Optimistic update
        setThemes(prev => prev.map(theme =>
            theme._id === id ? { ...theme, isActive: !currentStatus } : theme
        ));
        try {
            await axios.patch(`${BASE_URL}/api/master-theme/${id}/toggle-active`, {});
            toast.success(`Theme ${currentStatus ? 'deactivated' : 'activated'} successfully!`);
        } catch (err: any) {
            // Revert on error
            setThemes(prev => prev.map(theme =>
                theme._id === id ? { ...theme, isActive: currentStatus } : theme
            ));
            const errorMessage = err.response?.data?.message || 'Failed to update theme status';
            toast.error(errorMessage);
        } finally {
            setTogglingId(null);
        }
    };

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = themes.findIndex((item) => item._id === active.id);
            const newIndex = themes.findIndex((item) => item._id === over.id);

            // Optimistic UI update so the row follows the cursor immediately.
            const reordered = arrayMove(themes, oldIndex, newIndex);
            setThemes(reordered);

            try {
                // Send the target array index. The backend reassigns a contiguous
                // `order = index` sequence and returns the updated list, which we
                // use to sync local `order` fields (prevents stale-order bugs on
                // consecutive drags without a page refresh).
                const res = await axios.put(`${BASE_URL}/api/master-theme/${active.id}/reorder`, {
                    newIndex,
                });
                if (Array.isArray(res.data?.themes)) {
                    setThemes(res.data.themes);
                }
                toast.success('Theme reordered successfully!');
            } catch (err: any) {
                toast.error('Failed to reorder theme');
                fetchThemes();
            }
        }
    };

    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
                <h2 className="page-title">Master Theme Management</h2>
                <Dialog open={addOpen} onOpenChange={(open) => {
                    setAddOpen(open);
                    if (!open) {
                        setForm(emptyTheme);
                        resetUploadState();
                        setError('');
                    }
                }}>
                    <DialogTrigger asChild>
                        <Button>Add Theme</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>Add New Theme</DialogTitle>
                            <DialogDescription>Create a new theme with preview image and domain allocation</DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleAdd} className="space-y-5">
                            <div>
                                <label className="block text-sm font-medium mb-1">Theme Title</label>
                                <Input
                                    name="title"
                                    placeholder="Enter theme title"
                                    value={form.title}
                                    onChange={handleFormChange}
                                    required
                                />
                            </div>

                            <div>
                                <Label className="form-label">Theme Preview Image (16:9)</Label>
                                {uploadStep === 'idle' ? (
                                    !form.image ? (
                                        <div className="w-full">
                                            <Card
                                                className="group flex max-h-[200px] w-full flex-col items-center justify-center gap-4 py-8 border-dashed text-sm cursor-pointer hover:bg-secondary/50 transition-colors"
                                                onDragOver={(e: React.DragEvent<HTMLDivElement>) => {
                                                    e.preventDefault();
                                                    e.currentTarget.classList.add('bg-muted/50');
                                                }}
                                                onDragLeave={(e: React.DragEvent<HTMLDivElement>) => {
                                                    e.currentTarget.classList.remove('bg-muted/50');
                                                }}
                                                onDrop={(e: React.DragEvent<HTMLDivElement>) => {
                                                    e.preventDefault();
                                                    e.currentTarget.classList.remove('bg-muted/50');
                                                    const files = e.dataTransfer.files;
                                                    if (files.length > 0) {
                                                        const event = {
                                                            target: { files }
                                                        } as any;
                                                        handleFileChange(event);
                                                    }
                                                }}
                                                onClick={openFileUpload}
                                            >
                                                <div className="grid space-y-3">
                                                    <div className="flex items-center gap-x-2 text-muted-foreground justify-center">
                                                        <Upload className="size-5" />
                                                        <div>
                                                            Drop image here or{" "}
                                                            <Button
                                                                type="button"
                                                                variant="link"
                                                                className="text-primary p-0 h-auto font-normal"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    openFileUpload();
                                                                }}
                                                            >
                                                                browse
                                                            </Button>{" "}
                                                            to upload
                                                        </div>
                                                    </div>
                                                </div>
                                                <input
                                                    ref={fileInputRef}
                                                    type="file"
                                                    className="hidden"
                                                    accept=".png,.jpg,.jpeg"
                                                    onChange={handleFileChange}
                                                />
                                                <span className="text-xs text-muted-foreground mt-1 block">
                                                    Supported: JPG, PNG (max 2 MB) | Aspect ratio: 16:9
                                                </span>
                                            </Card>
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            <Card
                                                className="group flex max-h-[400px] w-full flex-col items-center justify-center gap-4 py-8 border-dashed text-sm cursor-pointer hover:bg-muted/50 transition-colors"
                                                onClick={openCropperForExisting}
                                            >
                                                <div 
                                                    className="relative max-h-64 w-full sm:max-w-64 rounded overflow-hidden"
                                                    style={{
                                                        backgroundImage: 'linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%, #e5e7eb), linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%, #e5e7eb)',
                                                        backgroundSize: '16px 16px',
                                                        backgroundPosition: '0 0, 8px 8px',
                                                        backgroundRepeat: 'repeat'
                                                    }}
                                                >
                                                    <img 
                                                        src={form.image} 
                                                        alt="uploaded" 
                                                        className="w-full h-full object-cover cursor-pointer hover:opacity-80 transition-opacity" 
                                                        title="Click to edit image"
                                                    />
                                                </div>
                                                <div className="flex items-center gap-x-2 text-muted-foreground text-xs">
                                                    <Upload className="size-4" />
                                                    <span>Click to edit or re-upload</span>
                                                </div>
                                            </Card>
                                            <div className="flex gap-2">
                                                <Button 
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    className="text-muted-foreground hover:text-foreground"
                                                    onClick={openFileUpload}
                                                >
                                                    <RefreshCw className="size-3.5" />
                                                    Change
                                                </Button>
                                                <Button 
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    className="text-muted-foreground hover:text-danger"
                                                    onClick={() => setForm(prev => ({ ...prev, image: '' }))}
                                                >
                                                    <Trash2 className="size-3.5" />
                                                    Remove
                                                </Button>
                                            </div>
                                            <input
                                                type="file"
                                                accept=".png,.jpg,.jpeg"
                                                onChange={handleFileChange}
                                                ref={fileInputRef}
                                                className="hidden"
                                            />
                                        </div>
                                    )
                                ) : uploadStep === 'cropping' && selectedFile ? (
                                    <div className="space-y-2">
                                        <Card className="flex flex-col items-center">
                                            <div className="w-full border-b p-3">
                                                <p className="text-sm font-medium text-foreground text-center">Adjust crop area (16:9 Aspect) - Drag to move, edges to resize</p>
                                            </div>
                                            <div className="w-full flex justify-center py-3">
                                                <Cropper
                                                    file={selectedFile}
                                                    aspect={16 / 9}
                                                    maxImageSize={2 * 1024 * 1024}
                                                    onCrop={handleCropComplete}
                                                    onCropRef={cropperRef}
                                                    keepSelection
                                                />
                                            </div>
                                        </Card>
                                        <div className="flex gap-2">
                                            <Button 
                                                type="button"
                                                onClick={async () => {
                                                    await cropperRef.current?.applyCrop();
                                                }}
                                                disabled={uploading}
                                                size="sm"
                                                loading={uploading}
                                                loadingText="Uploading..."
                                            >
                                                {form.image ? 'Update' : 'Upload'}
                                            </Button>
                                            <Button 
                                                type="button"
                                                variant="outline"
                                                onClick={resetUploadState}
                                                disabled={uploading}
                                                size="sm"
                                            >
                                                Cancel
                                            </Button>
                                        </div>
                                    </div>
                                ) : null}
                            </div>

                            <div className="flex items-center space-x-3">
                                <Switch
                                    checked={form.isPaid}
                                    onCheckedChange={(checked) => setForm((prev) => ({ ...prev, isPaid: checked, price: checked ? prev.price : 0 }))}
                                    aria-label="Toggle paid status"
                                />
                                <span className="text-sm font-medium">
                                    {form.isPaid ? 'Paid Theme' : 'Free Theme'}
                                </span>
                            </div>

                            {form.isPaid && (
                                <div>
                                    <label className="block text-sm font-medium mb-1">Price (USD)</label>
                                    <Input
                                        type="number"
                                        name="price"
                                        placeholder="0.00"
                                        value={form.price}
                                        onChange={handleFormChange}
                                        min="0"
                                        step="0.01"
                                        required={form.isPaid}
                                        prefix="$"
                                    />
                                </div>
                            )}

                            <div className="flex items-center space-x-3">
                                <Switch
                                    checked={form.isActive}
                                    onCheckedChange={(checked) => setForm((prev) => ({ ...prev, isActive: checked }))}
                                    aria-label="Toggle active state"
                                />
                                <span className={form.isActive ? 'status-label-active' : 'status-label-inactive'}>
                                    {form.isActive ? 'Active' : 'Inactive'}
                                </span>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2">Allocate to Domains</label>
                                <div className="space-y-2 max-h-48 overflow-y-auto border border-border rounded-lg p-3">
                                    {domains.length === 0 ? (
                                        <div className="text-sm text-muted-foreground">No active domains available</div>
                                    ) : (
                                        domains.map((domain) => (
                                            <div key={domain.slug} className="flex items-center space-x-2">
                                                <Checkbox
                                                    id={`domain-${domain.slug}`}
                                                    checked={form.allocatedDomains.includes(domain.slug)}
                                                    onCheckedChange={() => handleDomainToggle(domain.slug)}
                                                />
                                                <label
                                                    htmlFor={`domain-${domain.slug}`}
                                                    className="text-sm cursor-pointer flex-1"
                                                >
                                                    {domain.slug} <span className="text-muted-foreground">({domain.url})</span>
                                                </label>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>

                            {error && <div className="text-red-500 text-sm">{error}</div>}
                            <Button type="submit" loading={saving} loadingText="Adding...">Add Theme</Button>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            <div>
                {loading ? (
                    <PageLoader label="Loading themes..." />
                ) : themes.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-state-icon">
                            <Palette className="w-full h-full" />
                        </div>
                        <h3 className="empty-state-title">No themes found</h3>
                        <p className="empty-state-description">Create your first theme to customize the look and feel</p>
                    </div>
                ) : (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-muted/50">
                                    <TableHead className="w-10">#</TableHead>
                                    <TableHead className="w-20">Image</TableHead>
                                    <TableHead>Title</TableHead>
                                    <TableHead>Type</TableHead>
                                    <TableHead>Allocated Domains</TableHead>
                                    <TableHead>Created</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead className="w-24">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                <SortableContext
                                    items={themes.map((t) => t._id)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    {themes.map((theme) => (
                                        <SortableRow
                                            key={theme._id}
                                            theme={theme}
                                            onEdit={handleEdit}
                                            onDelete={setDeleteId}
                                            onToggleActive={handleToggleActive}
                                            onSetDefault={handleSetDefault}
                                            togglingId={togglingId}
                                            defaultThemeIds={defaultThemeIds}
                                            settingDefaultId={settingDefaultId}
                                        />
                                    ))}
                                </SortableContext>
                            </TableBody>
                        </Table>
                    </DndContext>
                )}
            </div>

            {/* Edit Dialog */}
            <Dialog open={editOpen} onOpenChange={(open) => {
                setEditOpen(open);
                if (!open) {
                    setEditId(null);
                    setForm(emptyTheme);
                    resetUploadState();
                    setError('');
                }
            }}>
                <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Edit Theme</DialogTitle>
                        <DialogDescription>Update theme information and domain allocation</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleEditSubmit} className="space-y-5">
                        <div>
                            <label className="block text-sm font-medium mb-1">Theme Title</label>
                            <Input
                                name="title"
                                placeholder="Enter theme title"
                                value={form.title}
                                onChange={handleFormChange}
                                required
                            />
                        </div>

                        <div>
                            <Label className="form-label">Theme Preview Image (16:9)</Label>
                            {uploadStep === 'idle' ? (
                                !form.image ? (
                                    <div className="w-full">
                                        <Card
                                            className="group flex max-h-[200px] w-full flex-col items-center justify-center gap-4 py-8 border-dashed text-sm cursor-pointer hover:bg-secondary/50 transition-colors"
                                            onDragOver={(e: React.DragEvent<HTMLDivElement>) => {
                                                e.preventDefault();
                                                e.currentTarget.classList.add('bg-muted/50');
                                            }}
                                            onDragLeave={(e: React.DragEvent<HTMLDivElement>) => {
                                                e.currentTarget.classList.remove('bg-muted/50');
                                            }}
                                            onDrop={(e: React.DragEvent<HTMLDivElement>) => {
                                                e.preventDefault();
                                                e.currentTarget.classList.remove('bg-muted/50');
                                                const files = e.dataTransfer.files;
                                                if (files.length > 0) {
                                                    const event = {
                                                        target: { files }
                                                    } as any;
                                                    handleFileChange(event);
                                                }
                                            }}
                                            onClick={openFileUpload}
                                        >
                                            <div className="grid space-y-3">
                                                <div className="flex items-center gap-x-2 text-muted-foreground justify-center">
                                                    <Upload className="size-5" />
                                                    <div>
                                                        Drop image here or{" "}
                                                        <Button
                                                            type="button"
                                                            variant="link"
                                                            className="text-primary p-0 h-auto font-normal"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                openFileUpload();
                                                            }}
                                                        >
                                                            browse
                                                        </Button>{" "}
                                                        to upload
                                                    </div>
                                                </div>
                                            </div>
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                className="hidden"
                                                accept=".png,.jpg,.jpeg"
                                                onChange={handleFileChange}
                                            />
                                            <span className="text-xs text-muted-foreground mt-1 block">
                                                Supported: JPG, PNG (max 2 MB) | Aspect ratio: 16:9
                                            </span>
                                        </Card>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        <Card
                                            className="group flex max-h-[400px] w-full flex-col items-center justify-center gap-4 py-8 border-dashed text-sm cursor-pointer hover:bg-muted/50 transition-colors"
                                            onClick={openCropperForExisting}
                                        >
                                            <div 
                                                className="relative max-h-64 w-full sm:max-w-64 rounded overflow-hidden"
                                                style={{
                                                    backgroundImage: 'linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%, #e5e7eb), linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%, #e5e7eb)',
                                                    backgroundSize: '16px 16px',
                                                    backgroundPosition: '0 0, 8px 8px',
                                                    backgroundRepeat: 'repeat'
                                                }}
                                            >
                                                <img 
                                                    src={form.image} 
                                                    alt="uploaded" 
                                                    className="w-full h-full object-cover cursor-pointer hover:opacity-80 transition-opacity" 
                                                    title="Click to edit image"
                                                />
                                            </div>
                                            <div className="flex items-center gap-x-2 text-muted-foreground text-xs">
                                                <Upload className="size-4" />
                                                <span>Click to edit or re-upload</span>
                                            </div>
                                        </Card>
                                        <div className="flex gap-2">
                                            <Button 
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="text-muted-foreground hover:text-foreground"
                                                onClick={openFileUpload}
                                            >
                                                <RefreshCw className="size-3.5" />
                                                Change
                                            </Button>
                                            <Button 
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="text-muted-foreground hover:text-danger"
                                                onClick={() => setForm(prev => ({ ...prev, image: '' }))}
                                            >
                                                <Trash2 className="size-3.5" />
                                                Remove
                                            </Button>
                                        </div>
                                        <input
                                            type="file"
                                            accept=".png,.jpg,.jpeg"
                                            onChange={handleFileChange}
                                            ref={fileInputRef}
                                            className="hidden"
                                        />
                                    </div>
                                )
                            ) : uploadStep === 'cropping' && selectedFile ? (
                                <div className="space-y-2">
                                    <Card className="flex flex-col items-center">
                                        <div className="w-full border-b p-3">
                                            <p className="text-sm font-medium text-foreground text-center">Adjust crop area (16:9 Aspect) - Drag to move, edges to resize</p>
                                        </div>
                                        <div className="w-full flex justify-center py-3">
                                            <Cropper
                                                file={selectedFile}
                                                aspect={16 / 9}
                                                maxImageSize={2 * 1024 * 1024}
                                                onCrop={handleCropComplete}
                                                onCropRef={cropperRef}
                                                keepSelection
                                            />
                                        </div>
                                    </Card>
                                    <div className="flex gap-2">
                                        <Button 
                                            type="button"
                                            onClick={async () => {
                                                await cropperRef.current?.applyCrop();
                                            }}
                                            disabled={uploading}
                                            size="sm"
                                            loading={uploading}
                                            loadingText="Uploading..."
                                        >
                                            {form.image ? 'Update' : 'Upload'}
                                        </Button>
                                        <Button 
                                            type="button"
                                            variant="outline"
                                            onClick={resetUploadState}
                                            disabled={uploading}
                                            size="sm"
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                </div>
                            ) : null}
                        </div>

                        <div className="flex items-center space-x-3">
                            <Switch
                                checked={form.isPaid}
                                onCheckedChange={(checked) => setForm((prev) => ({ ...prev, isPaid: checked, price: checked ? prev.price : 0 }))}
                                aria-label="Toggle paid status"
                            />
                            <span className="text-sm font-medium">
                                {form.isPaid ? 'Paid Theme' : 'Free Theme'}
                            </span>
                        </div>

                        {form.isPaid && (
                            <div>
                                <label className="block text-sm font-medium mb-1">Price (USD)</label>
                                <Input
                                    type="number"
                                    name="price"
                                    placeholder="0.00"
                                    value={form.price}
                                    onChange={handleFormChange}
                                    min="0"
                                    step="0.01"
                                    required={form.isPaid}
                                    prefix="$"
                                />
                            </div>
                        )}

                        <div className="flex items-center space-x-3">
                            <Switch
                                checked={form.isActive}
                                onCheckedChange={(checked) => setForm((prev) => ({ ...prev, isActive: checked }))}
                                aria-label="Toggle active state"
                            />
                            <span className={form.isActive ? 'status-label-active' : 'status-label-inactive'}>
                                {form.isActive ? 'Active' : 'Inactive'}
                            </span>
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-2">Allocate to Domains</label>
                            <div className="space-y-2 max-h-48 overflow-y-auto border border-border rounded-lg p-3">
                                {domains.length === 0 ? (
                                    <div className="text-sm text-muted-foreground">No active domains available</div>
                                ) : (
                                    domains.map((domain) => (
                                        <div key={domain.slug} className="flex items-center space-x-2">
                                            <Checkbox
                                                id={`edit-domain-${domain.slug}`}
                                                checked={form.allocatedDomains.includes(domain.slug)}
                                                onCheckedChange={() => handleDomainToggle(domain.slug)}
                                            />
                                            <label
                                                htmlFor={`edit-domain-${domain.slug}`}
                                                className="text-sm cursor-pointer flex-1"
                                            >
                                                {domain.slug} <span className="text-muted-foreground">({domain.url})</span>
                                            </label>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        {error && <div className="text-red-500 text-sm">{error}</div>}
                        <Button type="submit" loading={saving} loadingText="Saving...">Save Changes</Button>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <ConfirmDialog
                open={!!deleteId}
                onOpenChange={(open) => !open && setDeleteId(null)}
                title="Delete Theme"
                description="Are you sure you want to delete this theme? This action cannot be undone."
                confirmLabel="Delete"
                onConfirm={handleDelete}
            />
        </div>
    );
};

export default MasterThemeManager;
